// 接受邀請（SPEC #org-model-membership、AUTH-01；取代原「啟用工作帳號」）：
// 邀請連結 /activate#t=<token>（token 在 fragment、不進 log；舊連結 ?token= 也收；env 參數只從 /config.json 的 planes 挑，不接受連結帶網址）。
//   - 沒登入：〔我已經有帳號〕→ 記下 token、回登入頁，登入後殼層自動 POST /v1/orgs/join（lib/pendingJoin.ts）；
//            〔我還沒有帳號〕→ 設密碼 → POST /v1/orgs/join/register（identity 視邀請連結為 email 已驗證）→ 回登入頁。
//   - 已登入：POST /v1/orgs/join/preview 看邀請（組織、角色、邀請人、到期；email 只給遮罩）→〔接受〕POST /v1/orgs/join。
// 個人分享的邀請（沒有帳號的人被分享）也走同一個連結：後端直接讓分享生效（kind = share）。
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isKinioError } from '@kinio/web-sdk';
import { useApp } from '../../lib/AppContext';
import { inviteTokenFrom, isShareJoined, joinApi } from '../../lib/planeApi';
import { savePendingJoin } from '../../lib/pendingJoin';
import { useEmailVerifyPrompt } from '../../components/EmailVerifyPrompt';
import { logout } from '../../lib/session';
import { PasswordInput } from '../../components/PasswordInput';
import { stamp } from '../../lib/timefmt';

function errCode(e: unknown): string {
  if (isKinioError(e)) return e.code;
  return String((e as { code?: string })?.code ?? 'default');
}

export function ActivatePage() {
  const { t } = useTranslation();
  const { config, session } = useApp();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const token = useMemo(() => inviteTokenFrom(window.location), []);
  // 連結帶了 env（平台 H 起的邀請連結 …/i?env=<plane>#t=<token>）就只打那個 plane；這個 VMS 沒有那個 plane → 連結無效（不換別的 plane 試）
  const envParam = params.get('env');
  const env = envParam ?? config.env;
  const api = envParam ? (config.planes?.[envParam] ?? '') : (config.planes?.[env] ?? Object.values(config.planes ?? {})[0] ?? '');
  const brand = (
    <div className="login-brand">
      {config.theme.logo && <img src={config.theme.logo} alt="" />}
      <span>{config.display_name}</span>
    </div>
  );
  const body = !token || !api ? <p className="error">{t('activate.bad_link')}</p> : session ? <AcceptSignedIn api={api} token={token} /> : <AcceptSignedOut api={api} token={token} env={env} />;
  if (session)
    return (
      <section className="card" style={{ maxWidth: 560 }}>
        <h1 className="page-title">{t('activate.title')}</h1>
        {body}
      </section>
    );
  return (
    <main className="login">
      <div className="card login-card">
        {brand}
        <h2 className="section-title">{t('activate.title')}</h2>
        {body}
      </div>
    </main>
  );
}

function AcceptSignedOut({ api, token, env }: { api: string; token: string; env: string }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'pick' | 'register'>('pick');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<{ existing: boolean; email?: string; share: boolean; org?: string } | null>(null);

  function haveAccount() {
    savePendingJoin(token, env);
    window.location.assign('/');
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password !== password2) {
      setErr(t('activate.mismatch'));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const r = await joinApi.register(api, { token, password, display_name: name.trim() || undefined });
      setDone({ existing: !!r.existing_account, email: r.email, share: r.kind === 'share', org: r.org_name });
    } catch (x) {
      const c = errCode(x);
      setErr(t(`activate.err_${c}`, { code: c, defaultValue: t('activate.err_default', { code: c }) }));
    } finally {
      setBusy(false);
    }
  }

  if (done)
    return (
      <div className="form">
        <p className="notice">
          {done.existing ? t('activate.done_existing') : done.share ? t('activate.done_share') : t('activate.done', { email: done.email ?? '' })}
          {done.org && !done.share ? ' ' + t('activate.joined_org', { name: done.org }) : ''}
        </p>
        <a className="btn btn-primary" href="/">
          {t('activate.to_login')}
        </a>
      </div>
    );
  if (mode === 'pick')
    return (
      <div className="form">
        <p className="muted">{t('activate.body')}</p>
        <button className="btn btn-primary" onClick={haveAccount}>
          {t('activate.have_account')}
        </button>
        <button className="btn" onClick={() => setMode('register')}>
          {t('activate.no_account')}
        </button>
      </div>
    );
  return (
    <form className="form" onSubmit={submit}>
      <p className="muted">{t('activate.register_body')}</p>
      <label className="field">
        <span>{t('activate.display_name')}</span>
        <input className="input" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="field">
        <span>{t('auth.password')}</span>
        <PasswordInput autoComplete="new-password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} />
        <span className="small muted">{t('activate.password_hint')}</span>
      </label>
      <label className="field">
        <span>{t('activate.password_again')}</span>
        <PasswordInput autoComplete="new-password" required value={password2} onChange={(e) => setPassword2(e.target.value)} />
      </label>
      <span className="error small" role="status" data-pw-mismatch>
        {password2 && password !== password2 ? t('activate.mismatch') : ''}
      </span>
      {err && (
        <div className="error" role="alert">
          {err}
        </div>
      )}
      <div className="row-end">
        <button type="button" className="btn" onClick={() => setMode('pick')}>
          {t('common.back')}
        </button>
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {t('activate.submit')}
        </button>
      </div>
    </form>
  );
}

function AcceptSignedIn({ api, token }: { api: string; token: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const preview = useQuery({ queryKey: ['join-preview', token], queryFn: () => joinApi.preview(api, token), retry: false, staleTime: Infinity });
  // email 還沒驗證（403 EMAIL_NOT_VERIFIED，platform#176；分享邀請在 preview 就生效，所以 preview 也會遇到）→「先驗證你的 email」，
  // 對話框發現其實已驗證過 → 重試原本那一步
  const verify = useEmailVerifyPrompt(() => (preview.isError ? void preview.refetch() : accept.mutate()));
  const accept = useMutation({
    mutationFn: () => joinApi.join(api, token),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me-orgs'] });
      void qc.invalidateQueries({ queryKey: ['me-devices'] });
    },
    onError: (e) => verify.ask(e),
  });
  const previewErr = preview.error;
  const askVerify = verify.ask;
  useEffect(() => {
    if (previewErr) askVerify(previewErr);
  }, [previewErr, askVerify]);
  // 個人分享的邀請：preview 時後端就讓分享生效了（kind = share）→ 重列「我的設備」
  const shareDone = isShareJoined(preview.data) || isShareJoined(accept.data);
  useEffect(() => {
    if (shareDone) void qc.invalidateQueries({ queryKey: ['me-devices'] });
  }, [shareDone, qc]);
  if (preview.isLoading) return <p className="muted">{t('common.loading')}</p>;
  if (preview.isError) {
    const c = errCode(preview.error);
    return (
      <>
        {verify.prompt}
        <p className="error">{t(`activate.err_${c}`, { code: c, defaultValue: t('activate.err_default', { code: c }) })}</p>
      </>
    );
  }
  const p = preview.data;
  if (shareDone) {
    return (
      <div className="form">
        <p className="notice">{t('activate.done_share')}</p>
        <Link className="btn btn-primary" to="/devices">
          {t('activate.to_devices')}
        </Link>
      </div>
    );
  }
  if (!p || isShareJoined(p)) return null;
  if (accept.isSuccess)
    return (
      <div className="form">
        <p className="notice">{t('activate.joined', { name: p.org.display_name })}</p>
        <Link className="btn btn-primary" to="/">
          {t('activate.to_devices')}
        </Link>
      </div>
    );
  return (
    <div className="form">
      {verify.prompt}
      <table className="grid dinfo">
        <tbody>
          <tr>
            <th scope="row">{t('activate.org')}</th>
            <td>{p.org.display_name}</td>
          </tr>
          <tr>
            <th scope="row">{t('people.role')}</th>
            <td>{t(`people.org_role_${p.role}`, { defaultValue: p.role })}</td>
          </tr>
          {p.invited_by && (
            <tr>
              <th scope="row">{t('activate.invited_by')}</th>
              <td>{p.invited_by}</td>
            </tr>
          )}
          <tr>
            <th scope="row">{t('activate.invited_email')}</th>
            <td className="mono">{p.email_masked}</td>
          </tr>
          <tr>
            <th scope="row">{t('activate.expires')}</th>
            <td>{stamp(p.expires_at)}</td>
          </tr>
        </tbody>
      </table>
      {p.member_role && <p className="notice">{t('activate.already_member', { role: t(`people.org_role_${p.member_role}`, { defaultValue: p.member_role }) })}</p>}
      {!p.email_match ? (
        <>
          <p className="error" role="alert">
            {t('activate.err_INVITE_EMAIL_MISMATCH_masked', { email: p.email_masked })}
          </p>
          <button className="btn" onClick={() => void logout()}>
            {t('activate.switch_account')}
          </button>
        </>
      ) : (
        !p.member_role && (
          <button className="btn btn-primary" disabled={accept.isPending} onClick={() => accept.mutate()}>
            {t('activate.accept')}
          </button>
        )
      )}
      {accept.isError && (
        <p className="error" role="alert">
          {t(`activate.err_${errCode(accept.error)}`, { defaultValue: t('activate.err_default', { code: errCode(accept.error) }) })}
        </p>
      )}
    </div>
  );
}
